import type { ReactNode } from 'react';
import { ArrowLeft, FolderOpen, Maximize2 } from 'lucide-react';
import clsx from 'clsx';
interface Props {
  fullScreen: boolean;
  contextLabel: string;
  status: string;
  statusColour: string;
  buttonClass: string;
  toggleScreen: () => void;
  management: boolean;
  managementOpen: boolean;
  toggleManagement: () => void;
  tutor?: ReactNode;
}
export function WorkingCanvasHeader({
  fullScreen,
  contextLabel,
  status,
  statusColour,
  buttonClass,
  toggleScreen,
  management,
  managementOpen,
  toggleManagement,
  tutor,
}: Props) {
  return (
    <header
      className={clsx(
        'relative z-50 flex shrink-0 items-center gap-1.5 border-b border-hairline bg-canvas-pure px-2 md:gap-3 md:px-3',
        fullScreen ? 'min-h-16 pt-[env(safe-area-inset-top)]' : 'py-3',
      )}
    >
      {fullScreen && (
        <button
          data-close-working
          type="button"
          aria-label="Back to question"
          title="Back to question · Esc"
          className={buttonClass}
          onClick={toggleScreen}
        >
          <ArrowLeft size={20} />
        </button>
      )}
      <picture className="shrink-0">
        <source media="(max-width: 639px)" srcSet="/favicon-512.png" />
        <img
          src="/logo-black-horizontal-approved-20261021.png"
          alt="Airbotix"
          className="h-auto w-8 sm:w-[72px] md:w-28"
        />
      </picture>
      <div className="min-w-0 flex-1">
        <p className="hidden truncate text-[10px] font-bold uppercase tracking-widest text-slate2 md:block">
          {contextLabel}
        </p>
        <h3 className="truncate text-sm font-extrabold text-ink">My working</h3>
        <span
          role="status"
          title={status}
          className="flex min-w-0 items-center gap-1.5 text-[10px] font-semibold text-slate2"
        >
          <span className={clsx('h-1.5 w-1.5 shrink-0 rounded-full', statusColour)} />
          <span className="truncate">{status}</span>
        </span>
      </div>
      {tutor}
      {management && fullScreen && (
        <button
          type="button"
          aria-label="Manage my working"
          aria-expanded={managementOpen}
          title="My saved page"
          className={clsx(buttonClass, 'md:w-auto md:gap-2 md:px-3')}
          onClick={toggleManagement}
        >
          <FolderOpen size={20} />
          <span className="hidden text-xs font-bold md:inline">My page</span>
        </button>
      )}
      {!fullScreen && (
        <button
          type="button"
          data-open-working
          aria-label="Open full-screen working"
          title="Open full-screen working"
          className={clsx(buttonClass, 'bg-wash-sky text-ink')}
          onClick={toggleScreen}
        >
          <Maximize2 size={19} />
        </button>
      )}
    </header>
  );
}
